<script setup lang="ts">
import { Amplitude } from '@seede/amplitude';
import { t } from '~/i18n';

const email = ref('');
const password = ref('');
const error = ref('');
const isLoading = ref(false);
const showPassword = ref(false);
const { signUp, sendVerificationCode, user } = useAuth();

const emailCode = ref('');
const countdown = ref(0);
let timer: any = null;

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

definePageMeta({
  layout: 'auth',
});

// SEO: 注册页面 noindex（同 login）。
useHead({
  title: () => t('landingPages.auth.signup.seoDocumentTitle'),
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'robots', content: 'noindex, nofollow' },
    { name: 'title', content: () => t('landingPages.auth.signup.seoTitle') },
    { name: 'description', content: () => t('landingPages.auth.signup.seoDescription') },
  ],
});

const handleSendCode = async () => {
  error.value = '';
  if (!email.value) {
    error.value = t('landingPages.auth.common.emailRequired');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    error.value = t('landingPages.auth.common.emailInvalid');
    return;
  }

  try {
    await sendVerificationCode({ email: email.value });
    Amplitude.event('verification_sent', {
      method: 'email',
      scenario: 'signup',
    });
    countdown.value = 60;
    timer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(timer);
        timer = null;
      }
    }, 1000);
  } catch (err: any) {
    error.value = err.data?.message || err.message || t('landingPages.auth.common.sendCodeFailed');
  }
};

const handleSubmit = async () => {
  error.value = '';
  isLoading.value = true;

  try {
    if (!email.value) {
      error.value = t('landingPages.auth.common.emailRequired');
      isLoading.value = false;
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.value)) {
      error.value = t('landingPages.auth.common.emailInvalid');
      isLoading.value = false;
      return;
    }

    if (!password.value) {
      error.value = t('landingPages.auth.common.passwordRequired');
      isLoading.value = false;
      return;
    }

    if (password.value.length < 6) {
      error.value = t('landingPages.auth.common.passwordMinLength');
      isLoading.value = false;
      return;
    }

    if (!emailCode.value) {
      error.value = t('landingPages.auth.common.verificationCodeRequired');
      isLoading.value = false;
      return;
    }

    const inviteCodeCookie = useCookie('invite_code');
    const utmCookies = {
      source: useCookie('utm_source'),
      medium: useCookie('utm_medium'),
      campaign: useCookie('utm_campaign'),
      term: useCookie('utm_term'),
      content: useCookie('utm_content'),
    };

    await signUp({
      email: email.value,
      password: password.value,
      emailCode: emailCode.value,
      inviteCode: inviteCodeCookie.value || undefined,
      utm_source: utmCookies.source.value || undefined,
      utm_medium: utmCookies.medium.value || undefined,
      utm_campaign: utmCookies.campaign.value || undefined,
      utm_term: utmCookies.term.value || undefined,
      utm_content: utmCookies.content.value || undefined,
      language: 'en',
    });

    navigateTo('/workspace/create');
  } catch (err: any) {
    error.value = err.data?.message || err.message || t('landingPages.auth.common.genericError');
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <!-- Main Content -->
  <main class="flex-1 flex flex-col justify-center items-center relative z-10 px-4 py-8">
    <!-- Sign Up Form Container -->
    <div
      class="w-full max-w-[400px] mx-auto panel-gradient rounded-3xl px-6 pt-4 pb-6 shadow-lg backdrop-blur-[20px]"
    >
      <!-- eslint-disable-next-line vue/no-undef-components -->
      <AuthLoggedInView v-if="user" />

      <!-- Sign Up Form -->
      <div v-else class="w-full">
        <div class="flex items-center justify-between mb-16 md:mb-24">
          <h1 class="text-3xl md:text-4xl font-semibold tracking-tight text-black/90">
            {{ $t('landingPages.auth.signup.title') }}
          </h1>
          <NuxtLink to="/" class="inline-block transition-opacity duration-300">
            <img src="/veesoai-condense-dark.svg" alt="Veeso AI" class="h-8 w-auto" />
          </NuxtLink>
        </div>

        <!-- Sign Up Form -->
        <div class="flex flex-col gap-4">
          <!-- Google Sign Up Section -->
          <!-- eslint-disable-next-line vue/no-undef-components -->
          <AuthGoogleLoginButton />

          <!-- Divider -->
          <div class="relative flex items-center justify-center">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-black/10"></div>
            </div>
            <div class="relative px-4 text-sm font-normal text-black/40 bg-white rounded">
              {{ $t('landingPages.auth.common.or') }}
            </div>
          </div>

          <!-- Email/Password Form -->
          <form @submit.prevent="handleSubmit" class="flex flex-col gap-3">
            <!-- Email Input -->
            <input
              id="email"
              v-model="email"
              type="email"
              class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40"
              :placeholder="$t('landingPages.auth.common.email')"
              required
              autocomplete="email"
              @input="error = ''"
            />

            <!-- Verification Code Input -->
            <div class="flex gap-2">
              <input
                id="emailCode"
                v-model="emailCode"
                type="text"
                class="flex-1 min-w-0 h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40"
                :placeholder="$t('landingPages.auth.common.verificationCode')"
                required
                @input="error = ''"
              />
              <button
                type="button"
                @click="handleSendCode"
                :disabled="countdown > 0"
                class="shrink-0 h-10 px-4 flex items-center justify-center text-sm whitespace-nowrap font-normal bg-transparent border border-black/10 rounded-lg transition-all duration-200 hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="email ? 'text-black' : 'text-black/60'"
              >
                {{
                  countdown > 0
                    ? $t('landingPages.auth.common.countdownSeconds', { count: countdown })
                    : $t('landingPages.auth.common.sendCode')
                }}
              </button>
            </div>

            <!-- Password Input -->
            <div class="relative border border-black/10 rounded-lg">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="w-full h-10 px-4 pr-12 text-base font-normal text-black/90 bg-transparent transition-all duration-200 outline-none placeholder:text-black/40"
                :placeholder="$t('landingPages.auth.common.password')"
                required
                autocomplete="new-password"
                minlength="6"
                @input="error = ''"
              />
              <button
                type="button"
                @click="togglePasswordVisibility"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-black/60 hover:text-black/80 transition-colors duration-200"
                :aria-label="$t('landingPages.auth.common.togglePasswordVisibility')"
                tabindex="-1"
              ></button>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="w-full h-10 px-4 flex items-center justify-center text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 tracking-[0.02em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
              :disabled="isLoading"
            >
              {{
                isLoading
                  ? $t('landingPages.auth.signup.submitting')
                  : $t('landingPages.auth.signup.submit')
              }}
            </button>

            <!-- Error Message -->
            <div
              v-if="error"
              class="px-4 py-2.5 text-sm font-normal text-[#f90000] bg-[#f90000]/5 rounded-lg"
            >
              {{ error }}
            </div>

            <!-- Login Link -->
            <div class="text-left text-sm font-normal text-black/80 mt-2">
              {{ $t('landingPages.auth.common.hasAccount') }}
              <NuxtLink
                to="/login"
                class="text-black/80 underline ml-1 transition-colors hover:text-black"
              >
                {{ $t('landingPages.auth.common.logIn') }}
              </NuxtLink>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Terms Agreement Text -->
    <div class="mt-6 text-center text-xs font-normal text-white/50 px-4">
      {{ $t('landingPages.auth.common.termsPrefix') }}
      <NuxtLink to="/terms" class="text-white/50 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.termsOfService') }}
      </NuxtLink>
      {{ $t('landingPages.auth.common.and') }}
      <NuxtLink to="/privacy" class="text-white/50 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.privacyPolicy') }}
      </NuxtLink>
      .
    </div>
  </main>
</template>

<style scoped>
.panel-gradient {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 100%
  );
}

/* Prevent zoom on iOS */
@media (max-width: 640px) {
  input {
    font-size: 16px;
  }
}

/* Disable browser autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  box-shadow: 0 0 0 30px transparent inset !important;
  transition: background-color 5000s ease-in-out 0s;
}

/* Autofill text color */
input:-webkit-autofill {
  -webkit-text-fill-color: rgba(0, 0, 0, 0.9) !important;
}
</style>
